import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { FC, MouseEvent, useState } from 'react';
import styled from 'styled-components';
import Lightbox from 'yet-another-react-lightbox';
import Download from 'yet-another-react-lightbox/plugins/download';

import Text from '@/components/Text';
import useAlerts from '@/hooks/useAlerts';
import InvoicePdfModal from '@/pages/InvoiceList/components/InvoicePdfModal';
import { MediaResource } from '@/types/resources';
import { api } from '@/utils/api';

const Holder = styled.div`
  width: 100%;
  height: 36px;
  background: ${({ theme }) => theme.colors.grayscaleLight};
  color: ${({ theme }) => theme.colors.gray};
  border-radius: 4px;
  display: flex;
  padding: 6px 8px 6px 12px;
  align-items: center;
  justify-content: space-between;
`;

const FileName = styled(Text)`
  font-weight: 400;
  width: calc(100% - 50px);
  overflow: hidden;
  text-overflow: ellipsis;
  margin-right: 8px;
  white-space: nowrap;
`;

const ActionButton = styled.button`
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-left: 4px;
  background-color: ${({ theme }) => theme.colors.lightGray};
  border-radius: 50%;
  border: solid 1px transparent;
  transition: all ease-out 0.2s;
  color: ${({ theme }) => theme.colors.black};
  &:hover {
    color: ${({ theme }) => theme.colors.gray};
  }
`;

const NameHolder = styled.div`
  display: flex;
  align-items: center;
  width: calc(100% - 100px);
  > svg {
    margin-right: 6px;
  }
`;

const RemoveFileButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.colors.gray};
  transition: color ease-out 0.2s;
  &:hover {
    color: ${({ theme }) => theme.colors.error};
  }
`;

const RemoveHolder = styled.div`
  border-left: 1px solid #e9e9e9;
  padding-left: 6px;
  padding-right: 2px;
  margin-left: 12px;
`;

interface Props {
  file: MediaResource;
  canManage: boolean;
}

const FreelancerFileBlock: FC<Props> = ({ file, canManage }) => {
  const [showFileModal, setShowFileModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);

  const alerts = useAlerts();

  const regexForFileFormat = /(?:\.([^.]+))?$/;
  const fileFormat = regexForFileFormat.exec(file.name)?.[1];

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirm = await alerts.confirm(
        'Are you sure you want to delete this file?',
      );

      if (!confirm) return;

      await api.delete<never, { id: number }>(
        `/v1/payments/freelancers/files/${file.id}`,
      );

      queryClient.invalidateQueries(['freelancers_details'], {
        exact: false,
      });

      alerts.success('File was successfully deleted');
    } catch (e) {
      alerts.error();
    }
  };

  return (
    <Holder>
      <Lightbox
        styles={{
          root: {
            '--yarl__color_backdrop': 'rgba(0, 0, 0, .25)',
            '--yarl__carousel_padding_px': 40,
          },
        }}
        open={showImageModal}
        slides={[
          {
            src: file.versions.original,
            download: file.versions.original,
          },
        ]}
        close={() => setShowImageModal(false)}
        plugins={[Download]}
        render={{
          buttonPrev: () => null,
          buttonNext: () => null,
        }}
      />
      <NameHolder>
        <svg
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12.2839 6.57091L6.33334 12.6203C6.11901 12.8346 5.99861 13.1253 5.99861 13.4284C5.99861 13.7315 6.11901 14.0222 6.33334 14.2365C6.54767 14.4508 6.83836 14.5713 7.14146 14.5713C7.44457 14.5713 7.73526 14.4508 7.94959 14.2365L15.043 7.04429C15.2553 6.83204 15.4236 6.58007 15.5385 6.30275C15.6534 6.02544 15.7125 5.72821 15.7125 5.42805C15.7125 5.12788 15.6534 4.83066 15.5385 4.55334C15.4236 4.27603 15.2553 4.02405 15.043 3.8118C14.8308 3.59956 14.5788 3.43119 14.3015 3.31632C14.0241 3.20146 13.7269 3.14233 13.4268 3.14233C13.1266 3.14233 12.8294 3.20146 12.5521 3.31632C12.2747 3.43119 12.0228 3.59956 11.8105 3.8118L4.7171 11.004C4.07411 11.647 3.71289 12.5191 3.71289 13.4284C3.71289 14.3377 4.07411 15.2098 4.7171 15.8528C5.36008 16.4957 6.23215 16.857 7.14146 16.857C8.05078 16.857 8.92285 16.4957 9.56583 15.8528L15.4268 9.99948"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <FileName $type="smallMedium">{file.name}</FileName>
      </NameHolder>
      <ActionButton
        as="a"
        href={`${process.env.REACT_APP_API_ENDPOINT}/freelancer-files/${file.id}`}
        download
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M2 6.78571L6 10.7857L10 6.78571"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M6 1.64282V10.7857"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ActionButton>
      <ActionButton
        onClick={() => {
          if (fileFormat === 'pdf') {
            setShowFileModal(true);
          } else if (
            fileFormat === 'jpeg' ||
            fileFormat === 'png' ||
            fileFormat === 'jpg'
          ) {
            setShowImageModal(true);
          } else {
            return alerts.error(
              'File can’t be previewed',
              'File formats (.doc, .docx, .xls, .xlsx) is not valid for preview action.',
            );
          }
        }}
      >
        <svg
          width="12"
          height="12"
          viewBox="0 0 12 12"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M5.4375 9.375C7.61212 9.375 9.375 7.61212 9.375 5.4375C9.375 3.26288 7.61212 1.5 5.4375 1.5C3.26288 1.5 1.5 3.26288 1.5 5.4375C1.5 7.61212 3.26288 9.375 5.4375 9.375Z"
            stroke="currentCOlor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M8.22168 8.22192L10.4998 10.5001"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </ActionButton>
      {canManage && (
        <RemoveHolder>
          <RemoveFileButton onClick={onDelete}>
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M17 7L7 17"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M17 17L7 7"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </RemoveFileButton>
        </RemoveHolder>
      )}
      <AnimatePresence>
        {showFileModal && (
          <InvoicePdfModal
            file={file.versions.original}
            fileName={file.name}
            downloadLink={`${process.env.REACT_APP_API_ENDPOINT}/freelancer-files/${file.id}`}
            onClose={() => setShowFileModal(false)}
          />
        )}
      </AnimatePresence>
    </Holder>
  );
};

export default FreelancerFileBlock;
